import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { formatHoursToTimeString } from '@/helpers';
import { cn } from '@/lib/utils';

import { MatchedTimeOffDay } from '../../../helpers/time-off-request-match.helper';
import { TimeOffRequestSummaryStatus, TimeOffSummaryItem } from '../../../types/time-tracking';
import { TimeOffTooltipContent } from '../../time-off/time-off-tooltip-content';
import { TimeOffFallbackTooltip } from './timeoff-fallback-tooltip';
import { MIN_VISIBLE_HEIGHT_FRACTION, TWO_LINE_HEIGHT_FRACTION } from './timeoff-strip.helpers';

interface TimeOffDaySegmentProps {
  entry: TimeOffSummaryItem;
  /** Share of the cell height this segment occupies (drives 1- vs 2-line text). */
  heightFraction: number;
  /** The full request matched by date + status, when available (drives type + tooltip). */
  match?: MatchedTimeOffDay;
  isFirst: boolean;
}

/**
 * One proportional slice of a day cell: a colored band (approved solid /
 * pending stripes) whose flex-grow equals its hours, with an optional caption.
 * Hovering shows the rich request tooltip (or a minimal fallback). The caption
 * adapts to the available height — see {@link SegmentLabel}.
 */
export function TimeOffDaySegment({
  entry,
  heightFraction,
  match,
  isFirst,
}: TimeOffDaySegmentProps) {
  const isApproved = entry.status === TimeOffRequestSummaryStatus.APPROVED;
  const hoursLabel = formatHoursToTimeString(entry.totalHours);
  const typeLabel = match?.request.type.displayName;

  const isVisible = heightFraction >= MIN_VISIBLE_HEIGHT_FRACTION;
  const isTwoLine = heightFraction >= TWO_LINE_HEIGHT_FRACTION;

  const tooltip = match ? (
    <TimeOffTooltipContent request={match.request} day={match.day} />
  ) : (
    <TimeOffFallbackTooltip isApproved={isApproved} hoursLabel={hoursLabel} />
  );

  return (
    <SimpleTooltip content={tooltip} variant="light">
      <div
        className={cn(
          'group/timeoff relative flex min-h-0 cursor-default items-center justify-center overflow-hidden px-1 transition-[filter,transform] duration-150 ease-out hover:z-[1] hover:scale-y-[1.04] hover:brightness-110',
          !isFirst && 'border-t border-time-off-border/60'
        )}
        style={{ flex: `${entry.totalHours} 0 0` }}
      >
        <div
          aria-hidden="true"
          className={cn('absolute inset-0', isApproved ? 'time-off-approved' : 'time-off-pending')}
        />
        {isVisible && (
          <SegmentLabel typeLabel={typeLabel} hoursLabel={hoursLabel} isTwoLine={isTwoLine} />
        )}
      </div>
    </SimpleTooltip>
  );
}

/**
 * Segment caption. With room for two lines we stack the type above the hours;
 * otherwise a single "Type · Xh" line (hours only when the type is unknown).
 * The pending stripe pattern conveys status, so we never repeat it here.
 */
function SegmentLabel({
  typeLabel,
  hoursLabel,
  isTwoLine,
}: {
  typeLabel?: string;
  hoursLabel: string;
  isTwoLine: boolean;
}) {
  const baseClass = 'text-[11px] font-bold leading-tight text-time-off-text';

  if (isTwoLine && typeLabel) {
    return (
      <div className="relative z-[1] flex w-full flex-col items-center px-0.5">
        <span className={cn('line-clamp-1 w-full text-center', baseClass)}>{typeLabel}</span>
        <span className={cn('line-clamp-1', baseClass)}>{hoursLabel}</span>
      </div>
    );
  }

  return (
    <span className={cn('relative z-[1] line-clamp-1 px-0.5 text-center', baseClass)}>
      {typeLabel ? `${typeLabel} · ${hoursLabel}` : hoursLabel}
    </span>
  );
}
